import React from "react";
import { BrowserRouter, Routes, Route, Link, useLocation, Navigate, useNavigate } from "react-router-dom";

import MainMenuPage from "./pages/MainMenuPage";
import TmcPage from "./pages/TmcPage";
import CardCalc from "./Frontcalc/CardCalc";
import CalcJournal from "./Frontcalc/CalcJournal";
import TmcCard from "./Frontcalc/TmcCard";
import ObjectsPage from "./pages/ObjectsPage";
import ObjectCard from "./pages/ObjectCard";
import GroupsPage from "./pages/GroupsPage";
import GroupDetail from "./pages/GroupDetail";
import GroupSummary from "./pages/GroupSummary";
import Supercontrol from "./pages/Supercontrol";
import ClosingDocsPage from "./pages/ClosingDocsPage";
import TmcRequestsPage from "./pages/TmcRequestsPage";
import TmcRequestCard from "./pages/TmcRequestCard";
import WarehousePage from "./pages/WarehousePage";
import WarehouseObjectPage from "./pages/WarehouseObjectPage";
import SupercontrolPivot from "./pages/SupercontrolPivot";
import LoginPage from "./pages/LoginPage";
import UsersAdminPage from "./pages/UsersAdminPage";
import HrEmployeesPage from "./pages/HrEmployeesPage";
import EmployeeCard from "./pages/EmployeeCard";
import PositionsAdminPage from "./pages/PositionsAdminPage";
import PayrollRegistersPage from "./pages/PayrollRegistersPage";
import PayrollRegisterCard from "./pages/PayrollRegisterCard";
import InservLanding from "./pages/InservLanding";

import BrandWordmark from "./components/BrandWordmark";
import "./styles/brand-wordmark.css";
import "./styles/sticky-override.css";
import { resolveAccess, resolvePathModule } from "./utils/access";
import { useBreakpoint } from "./utils/useMediaQuery";
import { isTokenValid, clearAuthData } from "./utils/tokenUtils";

function TitleController() {
  const location = useLocation();

  React.useEffect(() => {
    document.title = resolveTitle(location.pathname);
  }, [location.pathname]);

  return null;
}

/* Router-aware BrandWordmark: скрывает базовый логотип на корне ("/"),
   делает его кликабельным (переход на "/") на остальных страницах */
function RouterAwareBrandWordmark() {
  const location = useLocation();
  if (location.pathname === "/") return null;
  return (
    <Link to="/" aria-label="Перейти в главное меню" className="brand-link">
      <BrandWordmark href="/" position="top-left" />
    </Link>
  );
}

/* ThemeToggle — использует window.Inserv API */
function ThemeToggle({ theme, onChange }) {
  const cycle = () => {
    const order = ["insrv", "dark", "auto"];
    const next = order[(order.indexOf(theme) + 1) % order.length] || "insrv";
    try { window.Inserv?.setTheme?.(next); } catch {}
    onChange(next);
  };

  return (
    <button
      onClick={cycle}
      title={`Тема: ${theme}`}
      className="theme-toggle"
      aria-label="Переключатель темы"
    >
      {theme === "insrv" ? "Светлая" : theme === "dark" ? "Тёмная" : "Авто"}
    </button>
  );
}

/* Header — теперь полностью скрывается на корне ("/") чтобы не просвечивать под заставкой */
function Header({ theme, onThemeChange }) {
  const location = useLocation();
  const navigate = useNavigate();
  const onRoot = location.pathname === "/";
  const onLogin = location.pathname.startsWith('/login');
  const onLanding = location.pathname.startsWith('/site-preview');
  const isCompact = useBreakpoint("lg");
  const [navOpen, setNavOpen] = React.useState(false);
  const [user, setUser] = React.useState(() => {
    try { return JSON.parse(localStorage.getItem('authUser') || 'null'); } catch { return null; }
  });

  // Обновляем пользователя при смене маршрута (например, после логина navigate('/'))
  React.useEffect(() => {
    try { setUser(JSON.parse(localStorage.getItem('authUser') || 'null')); } catch { setUser(null); }
  }, [location.pathname, location.search]);

  React.useEffect(() => {
    setNavOpen(false);
  }, [location.pathname, isCompact]);

  const toggleNav = () => setNavOpen((prev) => !prev);
  const closeNav = () => { if (isCompact) setNavOpen(false); };

  // Если мы на главном меню — не рендерим сам header вовсе
  if (onRoot || onLogin || onLanding) return null;

  const access = resolveAccess(user);
  const navLinks = [];
  if (access.canAccess.tmc) navLinks.push({ to: '/tmc', label: 'Справочник ТМЦ' });
  if (access.canAccess.tmcRequests) navLinks.push({ to: '/tmc-requests', label: 'Заявки на ТМЦ' });
  if (access.canAccess.warehouse) navLinks.push({ to: '/warehouse', label: 'Склад' });
  if (access.canAccess.cardCalc) navLinks.push({ to: '/card-calc', label: 'Журнал расчетных карточек' });
  if (access.canAccess.objects) navLinks.push({ to: '/objects', label: 'Журнал объектов' });
  if (access.canAccess.groups) navLinks.push({ to: '/groups', label: 'Журнал групп' });
  if (access.canAccess.supercontrol) navLinks.push({ to: '/supercontrol', label: 'Суперконтроль' });
  if (access.canAccess.closingDocs) navLinks.push({ to: '/closing-docs', label: 'Закрывающие документы' });
  if (access.canAccess.payrolls) navLinks.push({ to: '/payrolls', label: 'Ведомости' });
  if (access.canAccess.positions) navLinks.push({ to: '/positions', label: 'Должности' });
  if (access.canAccess.hr) navLinks.push({ to: '/hr/employees', label: 'Кадры' });
  if (access.canAccess.adminUsers) navLinks.push({ to: '/admin/users', label: 'Пользователи' });

  return (
    <header className={`app-header ${navOpen ? "nav-open" : ""}`}>
      <div className="header-left">
        <RouterAwareBrandWordmark />
      </div>

      {isCompact && (
        <button
          type="button"
          className="nav-toggle"
          aria-label={navOpen ? "Скрыть меню" : "Открыть меню"}
          aria-expanded={navOpen}
          onClick={toggleNav}
        >
          <span />
          <span />
          <span />
        </button>
      )}

      <nav className={`header-nav ${navOpen ? "is-open" : ""}`}>
        {navLinks.length === 0 ? (
          <span className="nav-note">Доступных разделов нет</span>
        ) : (
          navLinks.map((link) => (
            <Link key={link.to} to={link.to} className="nav-link" onClick={closeNav}>
              {link.label}
            </Link>
          ))
        )}
      </nav>

      <div className="header-right">
        <ThemeToggle theme={theme} onChange={onThemeChange} />
        <span className="user-chip">{user ? (user.name || user.id) : "Гость"}</span>
        {user && (
          <button
            className="logout-button"
            onClick={() => { localStorage.removeItem('authToken'); localStorage.removeItem('authUser'); navigate('/login'); }}
          >
            Выйти
          </button>
        )}
      </div>
    </header>
  );
}

export default function App() {
  const [theme, setTheme] = React.useState(() => {
    try { return window.Inserv?.getTheme?.() || "insrv"; } catch { return "insrv"; }
  });

  React.useEffect(() => {
    const onStorage = (e) => { if (e.key === "theme") setTheme(e.newValue || "insrv"); };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const handleThemeChange = (next) => setTheme(next);

  return (
    <BrowserRouter>
      <TitleController />
      <Header theme={theme} onThemeChange={handleThemeChange} />

      <main className="app-main" role="main">
        <Routes>
          <Route path="/login" element={<LoginPage />} />

          <Route path="/" element={<RequireAuth><MainMenuPage /></RequireAuth>} />
          <Route path="/tmc" element={<RequireAuth><TmcPage /></RequireAuth>} />
          <Route path="/tmc-requests" element={<RequireAuth><TmcRequestsPage /></RequireAuth>} />
          <Route path="/tmc-requests/new" element={<RequireAuth><TmcRequestCard isNew={true} /></RequireAuth>} />
          <Route path="/tmc-requests/writeoff/new" element={<RequireAuth><TmcRequestCard isNew={true} isWriteOff={true} /></RequireAuth>} />
          <Route path="/tmc-requests/:id" element={<RequireAuth><TmcRequestCard /></RequireAuth>} />
          <Route path="/tmc-requests/writeoff/:id" element={<RequireAuth><TmcRequestCard isWriteOff={true} /></RequireAuth>} />
          <Route path="/warehouse" element={<RequireAuth><GdOnly><WarehousePage /></GdOnly></RequireAuth>} />
          <Route path="/warehouse/:id" element={<RequireAuth><GdOnly><WarehouseObjectPage /></GdOnly></RequireAuth>} />
          <Route path="/card-calc" element={<RequireAuth><CalcJournal /></RequireAuth>} />
          <Route path="/card-calc/new" element={<RequireAuth><CardCalc isNew={true} /></RequireAuth>} />
          <Route path="/card-calc/:id" element={<RequireAuth><CardCalc /></RequireAuth>} />
          <Route path="/tmc/new" element={<RequireAuth><TmcCard /></RequireAuth>} />
          <Route path="/tmc/:id" element={<RequireAuth><TmcCard /></RequireAuth>} />
          <Route path="/objects" element={<RequireAuth><ObjectsPage /></RequireAuth>} />
          <Route path="/objects/:id" element={<RequireAuth><ObjectCard /></RequireAuth>} />
          <Route path="/groups" element={<RequireAuth><GroupsPage /></RequireAuth>} />
          <Route path="/groups/:id" element={<RequireAuth><GroupDetail /></RequireAuth>} />
          <Route path="/groups/:id/summary" element={<RequireAuth><GroupSummary /></RequireAuth>} />
          <Route path="/supercontrol" element={<RequireAuth><SuperOnly><Supercontrol /></SuperOnly></RequireAuth>} />
          <Route path="/supercontrol/pivot" element={<RequireAuth><SuperOnly><SupercontrolPivot /></SuperOnly></RequireAuth>} />
          <Route path="/closing-docs" element={<RequireAuth><SuperOnly><ClosingDocsPage /></SuperOnly></RequireAuth>} />
          <Route path="/payrolls" element={<RequireAuth><SuperOnly><PayrollRegistersPage /></SuperOnly></RequireAuth>} />
          <Route path="/payrolls/new" element={<RequireAuth><SuperOnly><PayrollRegisterCard /></SuperOnly></RequireAuth>} />
          <Route path="/payrolls/:id" element={<RequireAuth><SuperOnly><PayrollRegisterCard /></SuperOnly></RequireAuth>} />
          <Route path="/admin/users" element={<RequireAuth><AdminOnly><UsersAdminPage /></AdminOnly></RequireAuth>} />
          <Route path="/hr/employees" element={<RequireAuth><HrOrAdmin><HrEmployeesPage /></HrOrAdmin></RequireAuth>} />
          <Route path="/hr/employees/new" element={<RequireAuth><HrOrAdmin><EmployeeCard /></HrOrAdmin></RequireAuth>} />
          <Route path="/hr/employees/:id" element={<RequireAuth><HrOrAdmin><EmployeeCard /></HrOrAdmin></RequireAuth>} />
          <Route path="/positions" element={<RequireAuth><SuperOnly><PositionsAdminPage /></SuperOnly></RequireAuth>} />
          <Route path="/site-preview" element={<InservLanding />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
    </BrowserRouter>
  );
}

function RequireAuth({ children }) {
  const location = useLocation();
  const token = (typeof window !== 'undefined') ? localStorage.getItem('authToken') : null;
  
  // Если токена нет — перенаправляем на логин
  if (!token) {
    const from = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?from=${from}`} replace />;
  }
  
  // Если токен истек — очищаем данные авторизации и перенаправляем на логин
  if (!isTokenValid(token)) {
    clearAuthData();
    const from = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?from=${from}`} replace />;
  }
  
  try {
    const user = JSON.parse(localStorage.getItem('authUser') || 'null');
    const access = resolveAccess(user);
    const moduleKey = resolvePathModule(location.pathname);
    if (moduleKey && Object.prototype.hasOwnProperty.call(access.canAccess, moduleKey)) {
      if (!access.canAccess[moduleKey]) {
        const fallback = access.canAccess.tmc
          ? '/tmc'
          : access.canAccess.hr
            ? '/hr/employees'
            : '/';
        return <Navigate to={fallback} replace />;
      }
    }
  } catch {}
  return children;
}

function SuperOnly({ children }) {
  try {
    const user = JSON.parse(localStorage.getItem('authUser') || 'null');
    const access = resolveAccess(user);
    if (!access.isAdmin) return <Navigate to="/" replace />;
  } catch {}
  return children;
}

function AdminOnly({ children }) {
  try {
    const user = JSON.parse(localStorage.getItem('authUser') || 'null');
    const access = resolveAccess(user);
    if (!access.isAdmin) return <Navigate to="/" replace />;
  } catch {}
  return children;
}

function HrOrAdmin({ children }) {
  try {
    const user = JSON.parse(localStorage.getItem('authUser') || 'null');
    const access = resolveAccess(user);
    if (!access.canAccess.hr) return <Navigate to="/" replace />;
  } catch {}
  return children;
}

function GdOnly({ children }) {
  try {
    const user = JSON.parse(localStorage.getItem('authUser') || 'null');
    const access = resolveAccess(user);
    if (!access.canAccess.warehouse) return <Navigate to="/" replace />;
  } catch {}
  return children;
}

function resolveTitle(rawPath) {
  const base = '';
  const path = normalizePath(rawPath);

  if (path === '/login') return base + 'Вход';
  if (path === '/') return base + 'Главное меню';

  if (path === '/tmc') return base + 'Справочник ТМЦ';
  if (path === '/tmc/new') return base + 'Новая позиция ТМЦ';
  if (path.startsWith('/tmc/')) return base + 'Карточка ТМЦ';

  if (path === '/tmc-requests') return base + 'Заявки на ТМЦ';
  if (path === '/tmc-requests/new') return base + 'Новая заявка на ТМЦ';
  if (path.startsWith('/tmc-requests/')) return base + 'Карточка заявки ТМЦ';

  if (path === '/warehouse') return base + 'Склад';
  if (path.startsWith('/warehouse/')) return base + 'Склад объекта';

  if (path === '/card-calc') return base + 'Журнал расчетных карточек';
  if (path === '/card-calc/new') return base + 'Новая расчетная карточка';
  if (path.startsWith('/card-calc/')) return base + 'Расчетная карточка';

  if (path === '/objects') return base + 'Журнал объектов';
  if (path.startsWith('/objects/')) return base + 'Карточка объекта';

  if (path === '/groups') return base + 'Журнал групп';
  if (path.endsWith('/summary') && path.startsWith('/groups/')) return base + 'Сводка группы';
  if (path.startsWith('/groups/')) return base + 'Карточка группы';

  if (path === '/supercontrol') return base + 'Суперконтроль';
  if (path === '/supercontrol/pivot') return base + 'Пивот суперконтроля';
  if (path === '/closing-docs') return base + 'Реестр закрывающих документов';
  if (path === '/payrolls') return base + 'Журнал ведомостей';
  if (path === '/payrolls/new') return base + 'Новая ведомость';
  if (path.startsWith('/payrolls/')) return base + 'Карточка ведомости';

  if (path === '/positions') return base + 'Справочник должностей';
  if (path === '/admin/users') return base + 'Администрирование пользователей';

  if (path === '/hr/employees') return base + 'Журнал кадров';
  if (path === '/hr/employees/new') return base + 'Новый сотрудник';
  if (path.startsWith('/hr/employees/')) return base + 'Карточка сотрудника';

  return 'Портал';
}

function normalizePath(pathname) {
  if (!pathname || pathname === '/') return '/';
  return pathname.replace(/\/+$/,'');
}